import React, { useEffect, useContext, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import qs from 'query-string-for-all';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { AppContext } from '@prisjakt/ui-app-context';
import Breadcrumb from '@prisjakt/ui-breadcrumb';
import { PrimaryButton } from '@prisjakt/ui/button/variants';
import { storeLinkClick, adsViewUpdate } from './actions';
import { PageContent } from 'src/components/page';
import UserReviews from './components/user-reviews';
import { StickyTabNavigation } from '../../components/sticky-tab-nav';
import Header from './components/header';
import ReviewsHeader from './components/reviews-route/header';
import ReviewCardPlaceholder from '../../components/user-review-card/components/placeholder';
import useReviewData from '../reviews/hooks/data';
import {
  AnalyticsStore,
  NoResultTranslationKey,
  ReviewFilterOption,
  RouteSection,
  TabAnchor,
} from './types';
import { Store, UserReviewSummary } from '../../types';
import { UserReview } from '../reviews/types';
import { NotificationTypeEnum } from './components/notification/types';
import { NotificationTypes } from '../../components/notification/types';
import formatNumber from '../../globals/helpers/format-number';
import { formatDate } from './components/notification/utils';
import { AppState } from '../../reducers';
import { ShopReviewType, storeReviewPublished } from '../reviews/tracking';
import { REVIEWS_LIMIT } from './constants';
import * as S from './styles';
import { getOptions } from './components/review-filter/options';
import { UserReviewFilter } from './components/review-filter/user-review-filter';
import { Option } from 'src/components/filter-dropdown';
import { IconAnalytics } from '@prisjakt/ui/icon';

type Props = {
  data: {
    store: Store & {
      userReviews: {
        nodes: UserReview[];
        myReview: UserReview;
      };
    };
  };
  routeSection: RouteSection;
};

const StorePage = ({ routeSection = RouteSection.MAIN, data }: Props) => {
  const { market, t, linkTo } = useContext(AppContext);
  const dispatch = useDispatch();
  const location = useLocation();
  const { push } = useHistory();
  const { reviews, myReview } = useReviewData({
    initialNodes: data?.store?.userReviews?.nodes,
    initialMyReview: data?.store?.userReviews?.myReview,
    pageInfo: data?.store?.userReviews?.pageInfo,
    pageType: 'store',
  });
  const analyticsAccess = useSelector((state: AppState) => {
    return state.areas.account.me?.analyticsAccess?.stores?.find(
      (store: AnalyticsStore) => store.id === data.store.id
    );
  });
  const review = useSelector((state: AppState) => state.areas.review);
  const myUserId = useSelector((state: AppState) => state.areas.account.me?.id);
  const { store } = data;

  const sotyBadge = store.storeOfTheYearAwards?.length
    ? store.storeOfTheYearAwards[0]
    : null;

  const userReviewsSummary: UserReviewSummary =
    review.userReviews && review.userReviews.summary
      ? review.userReviews.summary
      : store.userReviewSummary;

  const reviewFilterOptions = getOptions(t, userReviewsSummary);

  const { notification, ...queryParams } = qs.parse(location.search);

  const defaultSelectedReviewFilter =
    reviewFilterOptions.find(opt => opt.value === queryParams.filter) ||
    reviewFilterOptions[0];

  const [selectedReviewFilter, setSelectedReviewFilter] = useState<
    ReviewFilterOption
  >(defaultSelectedReviewFilter);

  const getReviewsCount = (
    countTotal: number | null | undefined = userReviewsSummary.countTotal
  ) => (countTotal ? `(${countTotal >= 500 ? '500+' : countTotal})` : null);

  const reviewsCountTotal = getReviewsCount();

  useEffect(() => {
    // Explicitly tell ads to update visibility/load (needed when lazy loading pages)
    dispatch(adsViewUpdate());
    if (notification === 'verify-success') {
      storeReviewPublished({
        type: ShopReviewType.SHOP,
        storeId: data?.store?.id,
        storeName: data?.store?.name,
      });
    }
  }, []);

  const handleStoreLinkClick = () => {
    dispatch(storeLinkClick());
  };

  const storeParams = queryParams.filter
    ? { filter: queryParams.filter }
    : undefined;
  const storeUrl = linkTo('store', store, storeParams);

  const consumerInformation =
    store.consumerInformation && store.consumerInformation.length > 0
      ? {
          messages: store.consumerInformation.map((info, index) => ({
            message: info.message,
            time: info.time && formatDate(new Date(info.time)),
            id: info.time || index,
            title: t('store', 'consumer-information.title'),
          })),
        }
      : null;

  const handleCloseNotification = useCallback(() => {
    push(`${location.pathname}?${qs.stringify(queryParams)}`);
  }, [location.pathname, push, queryParams]);

  let verifyNotificationMessage;
  let verifyNotificationType = NotificationTypes.INFO;
  switch (notification) {
    case 'verify-success':
      verifyNotificationMessage = t('reviews', `notification.verify-success`);
      verifyNotificationType = NotificationTypes.INFO;
      break;
    case 'verify-fail':
      verifyNotificationMessage = t('reviews', `dialog.unknown-error`);
      verifyNotificationType = NotificationTypes.ISSUE;
      break;
    case 'delete-success':
      verifyNotificationMessage = t('reviews', `dialog.delete-success`);
      verifyNotificationType = NotificationTypes.INFO;
      break;
    default:
  }

  const onFilterChange = (option: Option, route?: RouteSection) => {
    push(
      linkTo(
        'store',
        store,
        {
          ...queryParams,
          offset: 0,
          filter: option.value,
        },
        route
      )
    );
    setSelectedReviewFilter(option);
  };

  return (
    <PageContent data-test="StorePage" withAds>
      <Breadcrumb
        homeText={t('common', 'breadcrumb.home')}
        path={[]}
        firstPathSelectable
      />

      {verifyNotificationMessage ? (
        <S.VerifyNotification
          type={verifyNotificationType}
          message={verifyNotificationMessage}
          onClose={handleCloseNotification}
        />
      ) : null}

      {analyticsAccess?.url ? (
        <S.AnalyticsNotification
          notification={{
            message: t('store', 'store.pj-analytics-settings'),
            link: {
              text: t('store', 'store.pj-analytics-link'),
              url: 'analyticsAccess.url',
              icon: <IconAnalytics size={16} color="maybeBlue700" />,
              isExternal: true,
            },
          }}
          type={NotificationTypeEnum.INFO}
        />
      ) : null}

      <S.ShopPage>
        {routeSection === RouteSection.MAIN && (
          <>
            <Header
              sotyBadge={sotyBadge}
              store={store}
              storeLinkClick={handleStoreLinkClick}
            />
            <StickyTabNavigation
              defaultActiveTabId={TabAnchor.REVIEWS}
              tabs={[
                {
                  id: TabAnchor.REVIEWS,
                  title: reviewsCountTotal
                    ? t('store', 'store-menu.reviews', {
                        count: reviewsCountTotal,
                      })
                    : t('store', 'header.reviews'),
                },
                {
                  id: TabAnchor.ABOUT,
                  title: t('store', 'store-menu.about'),
                },
              ]}
            />
          </>
        )}

        {routeSection === RouteSection.REVIEWS && (
          <ReviewsHeader
            sotyBadge={sotyBadge}
            store={store}
            storeUrl={storeUrl}
            titleNode={
              <S.SectionTitle>
                {t('store', 'header.reviews')}
                {userReviewsSummary.countTotal ? (
                  <S.Count>
                    {`(${formatNumber(
                      selectedReviewFilter.distribution ||
                        userReviewsSummary.countTotal,
                      market.code
                    )})`}
                  </S.Count>
                ) : null}
              </S.SectionTitle>
            }
          />
        )}

        <S.ReviewsSection id={TabAnchor.REVIEWS}>
          <S.Main>
            {routeSection === RouteSection.MAIN && (
              <>
                {consumerInformation && (
                  <S.NeutralNotification
                    notification={consumerInformation}
                    type={NotificationTypeEnum.NEUTRAL}
                  />
                )}

                <>
                  {userReviewsSummary.countTotal && reviews.length ? (
                    <>
                      {userReviewsSummary.countTotal > REVIEWS_LIMIT ? (
                        <UserReviewFilter
                          onFilterChange={onFilterChange}
                          reviewFilterOptions={reviewFilterOptions}
                          defaultSelectedReviewFilter={
                            defaultSelectedReviewFilter
                          }
                          route={RouteSection.MAIN}
                        />
                      ) : null}
                      {reviews?.map((userReview, index: number) => {
                        return (
                          index < REVIEWS_LIMIT &&
                          userReview && (
                            <S.UserReviewCard
                              belongsTo={{
                                id: store.id,
                                name: store.name,
                                type: 'store',
                              }}
                              createdAt={userReview.createdAtFromNow}
                              id={userReview.id}
                              key={userReview.id}
                              likes={userReview.likeScore}
                              purchaseDate={userReview.purchaseDate}
                              rating={userReview.rating}
                              reply={userReview.reply}
                              replyDate={userReview.replyDate}
                              text={userReview.reviewText}
                              type="store"
                              userAvatar={userReview.user?.avatar}
                              userDisplayName={userReview.user?.displayName}
                              userId={userReview.user?.id}
                              userReviewCount={userReview.user?.reviewCount}
                              store={store}
                              isMyReview={userReview.user?.id === myUserId}
                              hideStore
                              viaShop={userReview.verifiedPurchase}
                            />
                          )
                        );
                      })}

                      <>
                        {selectedReviewFilter.distribution &&
                        selectedReviewFilter.distribution > REVIEWS_LIMIT ? (
                          <S.ButtonWrapper>
                            <PrimaryButton
                              container={Link}
                              containerProps={{
                                to: linkTo(
                                  'store',
                                  store,
                                  queryParams,
                                  RouteSection.REVIEWS
                                ),
                              }}
                              style={{ alignSelf: 'center' }}
                            >
                              {t('reviews', 'button.see-all-reviews', {
                                count:
                                  getReviewsCount(
                                    selectedReviewFilter.distribution
                                  ) || '',
                              })}
                            </PrimaryButton>
                          </S.ButtonWrapper>
                        ) : null}
                      </>
                    </>
                  ) : (
                    [...new Array(2)].map((emptyReview, index) => (
                      <ReviewCardPlaceholder
                        key={index.toString()}
                        type="store"
                      />
                    ))
                  )}
                </>
              </>
            )}

            {routeSection === RouteSection.REVIEWS && (
              <div>
                {consumerInformation && (
                  <S.NeutralNotification
                    notification={consumerInformation}
                    type={NotificationTypeEnum.NEUTRAL}
                  />
                )}

                {userReviewsSummary.countTotal !== null &&
                userReviewsSummary.countTotal !== undefined &&
                userReviewsSummary.countTotal > REVIEWS_LIMIT ? (
                  <UserReviewFilter
                    onFilterChange={onFilterChange}
                    reviewFilterOptions={reviewFilterOptions}
                    defaultSelectedReviewFilter={defaultSelectedReviewFilter}
                    route={RouteSection.REVIEWS}
                  />
                ) : null}

                {reviews.length ? (
                  <UserReviews
                    groupByOldAndNew
                    belongsTo={{
                      id: store.id,
                      name: store.name,
                      type: 'store',
                    }}
                    pageInfo={store.userReviews?.pageInfo}
                    myReview={myReview}
                    nodes={store.userReviews.nodes}
                    store={store}
                    showAds={false}
                    totalCount={
                      selectedReviewFilter.distribution ||
                      userReviewsSummary.countTotal
                    }
                  />
                ) : (
                  <S.NeutralNotification
                    notification={{
                      messages: [
                        ...(selectedReviewFilter.value
                          ? [
                              {
                                message: t(
                                  'reviews',
                                  `rating.option-${selectedReviewFilter.value
                                    .toString()
                                    .toLowerCase()}-no-result` as NoResultTranslationKey
                                ),
                                id: selectedReviewFilter.value,
                              },
                            ]
                          : []),
                      ],
                    }}
                    type={NotificationTypeEnum.NEUTRAL}
                  />
                )}
              </div>
            )}
          </S.Main>

          <S.SideColumn
            store={store}
            userReviewsSummary={userReviewsSummary}
            myReview={myReview}
          />
        </S.ReviewsSection>

        {routeSection === RouteSection.MAIN && (
          <S.AboutSection id={TabAnchor.ABOUT}>
            <S.SectionTitle>{t('store', 'store-menu.about')}</S.SectionTitle>
            <S.About store={store} storeLinkClick={handleStoreLinkClick} />
          </S.AboutSection>
        )}
      </S.ShopPage>
    </PageContent>
  );
};

export default StorePage;
